import React from "react";
import styled, { css, useTheme } from "styled-components";
import { LanguageNameSpaces, ClientStatus } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import Text from "common/Typography/Text";
import Flex from "common/Boxes/Flex";
import { LARGE_MOBILE } from "common/constants";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import { ApiSlotGroup } from "features/users-management/types";
import RowActions, {
  RowActionsType,
} from "features/users-management/RowActions";
import AccountRow from "features/users-management/AccountRow";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { newLookTreeRow } from "features/users-management/newLookRows";

interface ApiSlotRowProps {
  slot: ApiSlotGroup;
  accountBalances: Record<number, number>;
  isPortfolioLoading: boolean;
}

const ApiSlotRow: React.FC<ApiSlotRowProps> = ({
  slot,
  accountBalances,
  isPortfolioLoading,
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.USERS_MANAGEMENT);
  const { isNewLook, typography } = useTheme();

  const activeAccountsInSlotCount = slot.accounts.filter(
    (account) => account.client_status === ClientStatus.ACTIVE
  ).length;

  // New look: the slot is a header row in the user's well (the exchange
  // logo under the user's avatar, its name in the name column), its accounts
  // the rows under it.
  if (isNewLook)
    return (
      <NewLookSlot>
        <NewLookSlotRow>
          <span />
          <NewLookLogo>
            <IconWithFallback
              coinOrExchangeName={slot.exchange}
              iconType={ICON_TYPES.EXCHANGE}
              pxIconSize={18}
            />
          </NewLookLogo>
          <NewLookNames>
            <NewLookName title={slot.exchange}>{slot.exchange}</NewLookName>
            <NewLookMeta>
              {activeAccountsInSlotCount > 0
                ? t("api_slot_label", { slotNumber: slot.slotNumber })
                : t("api_slot_unused_label")}
            </NewLookMeta>
          </NewLookNames>
          <span />
          <RowActions type={RowActionsType.SLOT} slot={slot} />
        </NewLookSlotRow>
        {slot.accounts.map((account, index) => (
          <AccountRow
            key={index}
            account={account}
            balance={accountBalances?.[account.client_id]}
            isFirst={index === 0}
            isLast={index === slot.accounts.length - 1}
            isPortfolioLoading={isPortfolioLoading}
            activeAccountsInSlotCount={activeAccountsInSlotCount}
          />
        ))}
      </NewLookSlot>
    );

  return (
    <SlotWrapper>
      <SlotHeader>
        <Flex $gap={8} style={{ width: "100%" }}>
          <RowLeft>
            {isNewLook ? (
              // Same column as the user's avatar, so the tree reads straight.
              <NewLookSlotIcon>
                <IconWithFallback
                  coinOrExchangeName={slot.exchange}
                  iconType={ICON_TYPES.EXCHANGE}
                  pxIconSize={20}
                  iconStyle={{ borderRadius: 4 }}
                />
              </NewLookSlotIcon>
            ) : (
              <IconWithFallback
                coinOrExchangeName={slot.exchange}
                iconType={ICON_TYPES.EXCHANGE}
                iconSize="xl"
                iconStyle={{ borderRadius: 4 }}
              />
            )}
            <SlotTitleContainer>
              <SlotTitle
                $weight={isNewLook ? typography.fontWeights.MEDIUM : 600}
                $ellipsis
              >
                {slot.exchange}
              </SlotTitle>
              <SlotLabel type="secondary">
                {activeAccountsInSlotCount > 0
                  ? t("api_slot_label", { slotNumber: slot.slotNumber })
                  : t("api_slot_unused_label")}
              </SlotLabel>
            </SlotTitleContainer>
          </RowLeft>
          <RowRight>
            <RowActions type={RowActionsType.SLOT} slot={slot} />
          </RowRight>
        </Flex>
      </SlotHeader>
      <AccountsList>
        {slot.accounts.map((account, index) => (
          <AccountRow
            key={index}
            account={account}
            balance={accountBalances?.[account.client_id]}
            isFirst={index === 0}
            isLast={index === slot.accounts.length - 1}
            isPortfolioLoading={isPortfolioLoading}
            activeAccountsInSlotCount={activeAccountsInSlotCount}
          />
        ))}
      </AccountsList>
    </SlotWrapper>
  );
};

export default ApiSlotRow;

const SlotWrapper = styled.div`
  border-top: 1px solid ${(p) => p.theme.colors.lightBorderGrey};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin: 0 8px;
      border-top: ${p.theme.borders.lightest};
    `}
`;
const SlotHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 24px 16px 52px;
  background: transparent;
  min-height: 68px;

  @media (max-width: ${LARGE_MOBILE}) {
    padding: 12px 6px 12px 16px;
    min-height: auto;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The icon lands under the user's avatar (row padding 8 + chevron 16
         + gap 12 = 36 from the row edge; this panel is inset by 8). */
      && {
        padding: 12px 0 12px 28px;
        min-height: 0;
      }
    `}
`;
const SlotTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;

  @media (max-width: ${LARGE_MOBILE}) {
    gap: 0;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      min-width: 0;
      && {
        gap: 2px;
      }
    `}
`;
const RowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  overflow: hidden;

  @media (max-width: ${LARGE_MOBILE}) {
    gap: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      gap: 12px;
      min-width: 0;
    `}
`;
const SlotTitle = styled(Text)`
  @media (max-width: ${LARGE_MOBILE}) {
    font-size: 13px;
  }
`;
const SlotLabel = styled(Text)`
  font-size: 13px;
  white-space: nowrap;

  @media (max-width: ${LARGE_MOBILE}) {
    font-size: 12px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      font-size: 12px;
      font-variant-numeric: tabular-nums;
    `}
`;
const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;
const AccountsList = styled.div`
  background: transparent;
  padding-left: 36px;

  @media (max-width: ${LARGE_MOBILE}) {
    padding-left: 50px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      && {
        padding-left: 0;
      }
    `}
`;
const NewLookSlotIcon = styled.span`
  flex: none;
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: ${(p) => p.theme.borders.lightest};
  background: ${(p) => p.theme.colors.cardBgColor};
  @media (max-width: 360px) {
    display: none;
  }
`;
const NewLookSlot = styled.div`
  padding: 4px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const NewLookSlotRow = styled.div`
  ${newLookTreeRow};
  min-height: 52px;
`;
const NewLookLogo = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
`;
const NewLookNames = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const NewLookName = styled.span`
  color: ${(p) => getNewLookTokens(p.theme).ink};
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const NewLookMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
